/**
 * BajaCorreosPage — /baja?id=<envío>&t=<firma>: dejar de recibir correos del proyecto.
 *
 * Abrir la página no da de baja (los antivirus y las vistas previas visitan los
 * enlaces solos): hay que pulsar el botón. También se puede deshacer.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE_URL, BACKEND_BASE } from '../services/api';
import { useProject } from '../contexts/ProjectContext';

const Pantalla = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.2rem;
  background: #f5f7fb;
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #0b1f3a;
`;

const Tarjeta = styled.div`
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08);
  padding: 2rem 1.8rem;
  text-align: center;
`;

const Logo = styled.img`
  max-height: 56px;
  max-width: 220px;
  margin-bottom: 1rem;
`;

const Titulo = styled.h1`
  margin: 0 0 0.6rem;
  font-size: 1.25rem;
  color: ${p => p.$color};
`;

const Texto = styled.p`
  margin: 0 0 1.2rem;
  color: #5b6779;
  font-size: 0.93rem;
  line-height: 1.55;
`;

const Boton = styled.button`
  width: 100%;
  border: ${p => (p.$secundario ? '1px solid #d5dbe5' : 'none')};
  border-radius: 8px;
  padding: 0.8rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  background: ${p => (p.$secundario ? '#fff' : p.$bg)};
  color: ${p => (p.$secundario ? '#334155' : '#fff')};
  &:disabled { opacity: 0.6; cursor: default; }
`;

const BajaCorreosPage = () => {
  const [params] = useSearchParams();
  const id = params.get('id') || '';
  const t = params.get('t') || '';
  const { proyecto } = useProject();
  const color = proyecto?.color_primario || '#0f355b';
  const logo = proyecto?.logo ? `${BACKEND_BASE}/${proyecto.logo}` : null;

  const [datos, setDatos] = useState(null);     // { proyecto, email, de_baja }
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [trabajando, setTrabajando] = useState(false);

  const url = `${API_BASE_URL}/baja.php?id=${encodeURIComponent(id)}&t=${encodeURIComponent(t)}`;

  useEffect(() => {
    if (!id || !t) { setError('Este enlace no es válido.'); return; }
    axios.get(url)
      .then(r => setDatos(r.data))
      .catch(e => setError(e.response?.data?.message || 'Este enlace no es válido.'));
  }, [url, id, t]);

  const accion = async (nombre) => {
    setTrabajando(true);
    try {
      const r = await axios.post(url, { accion: nombre });
      setDatos(prev => ({ ...prev, de_baja: r.data.de_baja }));
      setMensaje(r.data.message);
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo completar. Inténtalo de nuevo.');
    }
    setTrabajando(false);
  };

  return (
    <Pantalla>
      <Tarjeta>
        {logo && <Logo src={logo} alt="" />}

        {error && (
          <>
            <Titulo $color={color}>Enlace no válido</Titulo>
            <Texto>{error}</Texto>
          </>
        )}

        {!error && !datos && <Texto>Cargando…</Texto>}

        {!error && datos && (
          datos.de_baja ? (
            <>
              <Titulo $color={color}>Estás dado de baja</Titulo>
              <Texto>
                {mensaje || `Ya no recibes los avisos por correo de ${datos.proyecto}.`}
                {' '}Los correos que tú pidas, como recuperar la contraseña, te seguirán llegando.
              </Texto>
              <Boton $secundario disabled={trabajando} onClick={() => accion('reactivar')}>
                Me equivoqué, quiero volver a recibirlos
              </Boton>
            </>
          ) : (
            <>
              <Titulo $color={color}>¿Dejar de recibir correos?</Titulo>
              <Texto>
                {mensaje || <>La dirección <strong>{datos.email}</strong> dejará de recibir los avisos y recordatorios de <strong>{datos.proyecto}</strong>.</>}
              </Texto>
              <Boton $bg={color} disabled={trabajando} onClick={() => accion('confirmar')}>
                {trabajando ? 'Un momento…' : 'Sí, darme de baja'}
              </Boton>
            </>
          )
        )}
      </Tarjeta>
    </Pantalla>
  );
};

export default BajaCorreosPage;
